'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { organizations, projects } from '@/lib/api';
import { FolderKanban, Plus, ArrowRight, Building2, Calendar, Trash2, Search, Shield } from 'lucide-react';
import { DashboardSkeleton } from '@/components/ui/Skeleton';
import type { OrganizationWithCount } from '@/types';

interface Project {
  id: string;
  name: string;
  description: string | null;
  organizationId: string;
  framework: string;
  createdAt: string;
  latestScan?: {
    id: string;
    overallCompliance: number;
    sovereigntyScore: number;
    generatedAt: string;
  } | null;
  _count?: {
    assets: number;
    scanReports: number;
  };
}

interface ProjectWithOrg extends Project {
  organizationName: string;
}

// Framework tag helper
const getFrameworkTag = (framework: string | null | undefined) => {
  const fw = (framework || '').toUpperCase();
  const frameworkMap: Record<string, { label: string; color: string; icon: string }> = {
    'GDPR': { label: 'GDPR', color: 'bg-blue-100 text-blue-700', icon: '🔒' },
    'NIS2': { label: 'NIS2', color: 'bg-purple-100 text-purple-700', icon: '🛡️' },
    'DORA': { label: 'DORA', color: 'bg-green-100 text-green-700', icon: '⚡' },
    'EPRIVACY': { label: 'ePrivacy', color: 'bg-orange-100 text-orange-700', icon: '🍪' },
    'AI_ACT': { label: 'AI Act', color: 'bg-pink-100 text-pink-700', icon: '🤖' },
    'ISO27001': { label: 'ISO 27001', color: 'bg-indigo-100 text-indigo-700', icon: '📋' },
    'SOC2': { label: 'SOC 2', color: 'bg-cyan-100 text-cyan-700', icon: '✓' },
    'HIPAA': { label: 'HIPAA', color: 'bg-red-100 text-red-700', icon: '🏥' },
    'PCI_DSS': { label: 'PCI DSS', color: 'bg-amber-100 text-amber-700', icon: '💳' },
  };
  return frameworkMap[fw] || { label: fw || 'Unknown', color: 'bg-gray-100 text-gray-600', icon: '📄' };
};

// Compliance score color helper
const getComplianceScoreStyle = (score: number | null | undefined) => {
  if (score === null || score === undefined) return { color: 'text-gray-400', bg: 'bg-gray-100', label: 'No scan' };
  if (score >= 80) return { color: 'text-green-600', bg: 'bg-green-100', label: 'Excellent' };
  if (score >= 60) return { color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Good' };
  if (score >= 40) return { color: 'text-orange-600', bg: 'bg-orange-100', label: 'Fair' };
  return { color: 'text-red-600', bg: 'bg-red-100', label: 'Needs Work' };
};

export default function ProjectsPage() {
  const [projectList, setProjectList] = useState<ProjectWithOrg[]>([]);
  const [orgList, setOrgList] = useState<OrganizationWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [frameworkFilter, setFrameworkFilter] = useState<string>('all');
  const [deletingProject, setDeletingProject] = useState<ProjectWithOrg | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const orgResult = await organizations.list();
    if (orgResult.data && Array.isArray(orgResult.data)) {
      setOrgList(orgResult.data);

      const allProjects: ProjectWithOrg[] = [];
      for (const org of orgResult.data) {
        const projectResult = await projects.list(org.id);
        if (projectResult.data && Array.isArray(projectResult.data)) {
          const projectsWithOrg = (projectResult.data as Project[]).map(p => ({
            ...p,
            organizationName: org.name
          }));
          allProjects.push(...projectsWithOrg);
        }
      }
      allProjects.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setProjectList(allProjects);
    }
    setLoading(false);
  };

  const handleDeleteProject = async () => {
    if (!deletingProject) return;
    setDeleteLoading(true);
    const result = await projects.delete(deletingProject.id);
    setDeleteLoading(false);
    if (!result.error) {
      setDeletingProject(null);
      loadData();
    } else {
      alert(result.error);
    }
  };

  // Get unique frameworks for filter
  const uniqueFrameworks = Array.from(new Set(projectList.map(p => p.framework).filter(Boolean)));

  const filteredProjects = projectList.filter(project => {
    const matchesSearch = 
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (project.description?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      project.organizationName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFramework = frameworkFilter === 'all' || project.framework === frameworkFilter;
    return matchesSearch && matchesFramework;
  });

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50 to-emerald-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-8">
          <div className="px-8 py-6 bg-gradient-to-r from-green-50 to-emerald-50">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg shadow-green-500/25">
                  <FolderKanban className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
                  <p className="text-sm text-gray-600">{projectList.length} project{projectList.length !== 1 ? 's' : ''} across all organizations</p>
                </div>
              </div>
              {orgList.length > 0 && (
                <Link
                  href={`/projects/new?org=${orgList[0]?.id || ''}`}
                  className="group inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-green-500/25 hover:scale-105 transition-all"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  New Project
                  <ArrowRight className="h-4 w-4 ml-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              )}
            </div>
          </div>

          {/* Search and Filters */}
          <div className="px-8 py-4 border-b border-gray-100">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search projects by name, description, or organization..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              {uniqueFrameworks.length > 0 && (
                <select
                  value={frameworkFilter}
                  onChange={(e) => setFrameworkFilter(e.target.value)}
                  className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                >
                  <option value="all">All Frameworks</option>
                  {uniqueFrameworks.map(fw => (
                    <option key={fw} value={fw}>{getFrameworkTag(fw).label}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>

        {/* Projects Grid */}
        {filteredProjects.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
            <FolderKanban className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            {searchQuery || frameworkFilter !== 'all' ? (
              <>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No projects found</h3>
                <p className="text-gray-500 mb-6">No projects match your filters</p>
                <button
                  onClick={() => { setSearchQuery(''); setFrameworkFilter('all'); }}
                  className="text-green-600 hover:text-green-700 font-medium"
                >
                  Clear filters
                </button>
              </>
            ) : orgList.length === 0 ? (
              <>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No organizations yet</h3>
                <p className="text-gray-500 mb-6">Create an organization first to add projects.</p>
                <Link
                  href="/organizations/new"
                  className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Create Organization
                </Link>
              </>
            ) : (
              <>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No projects yet</h3>
                <p className="text-gray-500 mb-6">Create your first project to start tracking compliance.</p>
                <Link
                  href={`/projects/new?org=${orgList[0]?.id || ''}`}
                  className="inline-flex items-center px-5 py-2.5 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Create Project
                </Link>
              </>
            )}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredProjects.map((project) => {
              const frameworkTag = getFrameworkTag(project.framework);
              const complianceScore = project.latestScan?.overallCompliance;
              const scoreStyle = getComplianceScoreStyle(complianceScore);
              const daysOld = Math.floor((Date.now() - new Date(project.createdAt).getTime()) / (1000 * 60 * 60 * 24));
              const isNew = daysOld < 7;

              return (
                <div
                  key={project.id}
                  className="group relative bg-gradient-to-br from-slate-50 to-green-50 rounded-2xl border-2 border-gray-100 hover:border-green-300 transition-all duration-300 overflow-hidden hover:shadow-xl hover:shadow-green-500/10"
                >
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500" />

                  <Link href={`/projects/${project.id}`} className="block p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <div className="p-2.5 bg-white rounded-xl shadow-sm group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                          <FolderKanban className="h-6 w-6 text-green-600" />
                        </div>
                        {isNew && (
                          <span className="px-2 py-0.5 bg-green-500 text-white rounded text-xs font-bold uppercase">
                            New
                          </span>
                        )}
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 ${frameworkTag.color} rounded-full text-xs font-medium`}>
                        <span>{frameworkTag.icon}</span>
                        {frameworkTag.label}
                      </span>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-green-700 transition-colors">
                      {project.name}
                    </h3>
                    {project.description ? (
                      <p className="text-sm text-gray-500 mb-4 line-clamp-2">{project.description}</p>
                    ) : (
                      <p className="text-sm text-amber-500 mb-4 italic">No description yet</p>
                    )}

                    {/* Compliance Score Display */}
                    <div className={`flex items-center justify-between p-3 ${scoreStyle.bg} rounded-lg mb-4`}>
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-gray-500" />
                        <span className="text-xs font-medium text-gray-600">Compliance Score</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {complianceScore !== undefined && complianceScore !== null ? (
                          <>
                            <span className={`text-lg font-bold ${scoreStyle.color}`}>
                              {Math.round(complianceScore)}%
                            </span>
                            <span className={`text-[10px] font-medium ${scoreStyle.color} opacity-70`}>
                              {scoreStyle.label}
                            </span>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 italic">No scan yet</span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(project.createdAt).toLocaleDateString()}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 rounded">
                        <Building2 className="h-3 w-3" />
                        {project.organizationName}
                      </span>
                      {project._count && (
                        <span className="text-gray-400">
                          {project._count.assets} asset{project._count.assets !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </Link>
                  <div className="px-5 pb-5 pt-3 border-t border-gray-200 flex items-center justify-between">
                    <Link href={`/projects/${project.id}`} className="flex items-center text-green-600 text-sm font-medium hover:text-green-700 transition-colors">
                      Open Project
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Link>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDeletingProject(project);
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete project"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Project Modal */}
      {deletingProject && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Project</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <span className="font-semibold">{deletingProject.name}</span>? 
              This will also delete all scan reports and assets associated with it. 
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeletingProject(null)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProject}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
